import { useEffect, useLayoutEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Button, TextField } from '@shared/design-system/ui'
import { AppError } from '@shared/api/error'
import { toast } from '@shared/lib/toast/toast-store'
import {
  useLoginMemberPassword,
  useMemberBrandQuery,
  useRequestMemberLogin,
  useVerifyMemberCode,
} from '../api/member-access.queries'
import { readStoredMemberSession, useMemberSession } from '../model/member-session-store'
import { MemberAccessLayout } from './MemberAccessLayout'
import { form, loginPanel, subtitle, title } from './member-access.css'

export function MemberLoginPage({ producerId }: { producerId: string }) {
  const navigate = useNavigate()
  const setSession = useMemberSession((state) => state.setSession)
  const token = useMemberSession((state) => state.token)
  const member = useMemberSession((state) => state.member)
  const stored = token ? { token, member, producerName: useMemberSession.getState().producerName } : readStoredMemberSession()
  const signedIn = Boolean(stored.token && stored.member?.producerId === producerId)
  const requestLogin = useRequestMemberLogin()
  const verifyCode = useVerifyMemberCode()
  const loginPassword = useLoginMemberPassword()
  const brand = useMemberBrandQuery(producerId)
  const producerName = brand.data?.producerName ?? 'Area de membros'
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [forgot, setForgot] = useState(false)
  const [codeSent, setCodeSent] = useState(false)

  useLayoutEffect(() => {
    if (!signedIn || !stored.token || !stored.member) return
    if (!token) {
      setSession(stored.token, stored.member, stored.producerName ?? 'Area de membros')
    }
  }, [setSession, signedIn, stored.member, stored.producerName, stored.token, token])

  useEffect(() => {
    if (!signedIn) return
    void navigate({ to: '/acesso/$producerId/biblioteca', params: { producerId }, replace: true })
  }, [navigate, producerId, signedIn])

  function goHome(session: { token: string; member: Parameters<typeof setSession>[1]; producerName: string }) {
    setSession(session.token, session.member, session.producerName)
    void navigate({ to: '/acesso/$producerId/biblioteca', params: { producerId }, replace: true })
  }

  if (signedIn) {
    return null
  }

  return (
    <MemberAccessLayout producerName={producerName}>
      <section className={loginPanel}>
        <div>
          <h1 className={title}>Entrar em {producerName}</h1>
          <p className={subtitle}>
            {forgot
              ? 'Informe o e-mail da compra. Enviamos um codigo de 6 digitos.'
              : 'Use o e-mail e a senha que chegaram no e-mail da compra.'}
          </p>
        </div>
        {forgot ? (
          <form
            className={form}
            onSubmit={(event) => {
              event.preventDefault()
              void (async () => {
                try {
                  if (!codeSent) {
                    await requestLogin.mutateAsync({ producerId, email })
                    setCodeSent(true)
                    toast.success('Codigo enviado.')
                    return
                  }
                  goHome(await verifyCode.mutateAsync({ producerId, email, code }))
                } catch (error) {
                  toast.error({
                    title: 'Nao foi possivel entrar',
                    description: error instanceof AppError ? error.message : 'Tente de novo.',
                  })
                }
              })()
            }}
          >
            <TextField label="E-mail" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            {codeSent ? (
              <TextField
                label="Codigo"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                required
              />
            ) : null}
            <Button type="submit" fullWidth disabled={requestLogin.isPending || verifyCode.isPending}>
              {codeSent ? 'Entrar' : 'Enviar codigo'}
            </Button>
            {codeSent ? (
              <Button
                type="button"
                variant="text"
                fullWidth
                disabled={requestLogin.isPending}
                onClick={() => {
                  void requestLogin.mutateAsync({ producerId, email }).then(() => {
                    toast.success('Codigo reenviado.')
                  })
                }}
              >
                Reenviar codigo
              </Button>
            ) : null}
            <Button type="button" variant="text" fullWidth onClick={() => setForgot(false)}>
              Voltar ao login
            </Button>
          </form>
        ) : (
          <form
            className={form}
            onSubmit={(event) => {
              event.preventDefault()
              void (async () => {
                try {
                  goHome(await loginPassword.mutateAsync({ producerId, email, password }))
                } catch (error) {
                  toast.error({
                    title: 'Nao foi possivel entrar',
                    description: error instanceof AppError ? error.message : 'Confira a senha.',
                  })
                }
              })()
            }}
          >
            <TextField label="E-mail" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            <TextField
              label="Senha"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <Button type="submit" fullWidth disabled={loginPassword.isPending}>
              Entrar
            </Button>
            <Button type="button" variant="text" fullWidth onClick={() => setForgot(true)}>
              Esqueci minha senha
            </Button>
          </form>
        )}
      </section>
    </MemberAccessLayout>
  )
}
